/** Map home data, prefetched by the shell and handed to MapHome (mid-ktc), and the
 *  return-visit cache (mid-e98.4): the last map a buyer saw, painted at once next time.
 *  Three independent requests, so pins never wait for the commute zone (up to 12 s).
 *  Scoped to one buyer and one session (lib/api.js sessionGeneration): a prefetch made
 *  for buyer A, or before a login or logout, is never handed out, its late responses
 *  reject instead of resolving, and they write no cache. */
import api, { buyerAPI, sessionGeneration } from '../lib/api';

let slot = null;

// ponytail: one localStorage entry per buyer; the pins the buyer already sees (title, price,
// score, coordinates), the commute zone and the map-sized summary. Cleared on logout.
const CACHE_PREFIX = 'mid_map_cache:';

export const readMapCache = (userId) => {
  if (!userId) return null;
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_PREFIX + userId));
    return Array.isArray(cached?.pins?.properties) ? cached : null;
  } catch {
    return null;
  }
};

/** Merge into the buyer's cache, only while the session that fetched it is still current. */
export const writeMapCache = (userId, generation, patch) => {
  if (!userId || generation !== sessionGeneration()) return;
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_PREFIX + userId)) || {};
    localStorage.setItem(CACHE_PREFIX + userId, JSON.stringify({ ...cached, ...patch }));
  } catch { /* storage full or blocked: the map still loads from the network */ }
};

export const clearMapCaches = () => {
  // A mode switch calls this (BuyerModeProvider): the shell's prefetch is the old mode's
  // listings too, and the remounted MapHome would take it (mid-zr03.17).
  slot = null;
  try {
    Object.keys(localStorage).filter((k) => k.startsWith(CACHE_PREFIX)).forEach((k) => localStorage.removeItem(k));
  } catch { /* nothing to clear */ }
};

export const loadMapData = (userId) => {
  const generation = sessionGeneration();
  const current = (request, cacheAs) => request.then(({ data }) => {
    if (generation !== sessionGeneration()) throw new Error('stale map data: the session changed');
    if (cacheAs) writeMapCache(userId, generation, { [cacheAs]: data });
    return data;
  });
  slot = {
    userId,
    generation,
    pins: current(api.get('/properties/compatible?limit=100'), 'pins'),
    zone: current(buyerAPI.getCommuteZone(), 'zone'),
    verify: current(api.get('/analysis-feedback/pending-verification')),
  };
  // A prefetch nobody takes must not surface as an unhandled rejection.
  [slot.pins, slot.zone, slot.verify].forEach((p) => p.catch(() => {}));
  return slot;
};

/** The shell's prefetch when it belongs to this buyer and session (used once), else a fresh load. */
export const takeMapData = (userId) => {
  const usable = slot && slot.userId === userId && slot.generation === sessionGeneration();
  const data = usable ? slot : loadMapData(userId);
  slot = null;
  return data;
};

/** Drop an untaken prefetch: the map will not mount (no Buyer DNA, summary failed) or the session changed. */
export const resetMapData = () => { slot = null; };
